/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Loader from "../../../common/loader/Loader";
import WidgetEmpty from "../empty-widget-content/empty-widget-content";
import TaskListItem from "./task-list-item/TaskListItem";
import WidgetOptions from "../widget-options/widget-options";
import TextOverflowTooltip from "../../../common/TextOverflowTooltip";
import IconButton from "../../../common/icon-button/icon-button";
import ModalDefault from "../../../common/modal/modal-default";
import ModalDelete from "../../../common/modal/modal-delete";
import TaskListItemDetails from "./task-list-item-details/TaskListItemDetails";
import TaskListConfigForm from "./TaskListConfigForm";
import TaskListNewTaskForm from "./TaskListNewTaskForm";
import UserService from "../../../../services/user-service";
import SortOptions from "./sort-options/SortOptions";
import { steveDataStorage } from "../../../../helpers/sdk-helpers";
import "./task-list.scss";
import {
  getTasksSortingFunction,
  PRIORITY_DESCENDING,
} from "../../../../helpers/sort-helper";

const TaskList = ({
  showWidgetInfo,
  onRemove,
  onClone,
  onUpdate,
  isEditAllowed,
  height,
  width,
  data,
}) => {
  const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
  const [isTaskDetailsModalShown, setIsTaskDetailsModalShown] = useState(false);
  const [isTaskConfigShown, setIsTaskConfigShown] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [formData, setFormData] = useState({
    title: data.title,
    assignedTo: data.config.assignedTo || "",
    priority: data.config.priority || [],
    status: data.config.status || [],
    creator: data.config.creator || "",
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [taskListFeed, setTaskListFeed] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedSort, setSelectedSort] = useState(PRIORITY_DESCENDING);
  const [createdTaskId, setCreatedTaskId] = useState(null);
  const [isListEmpty, setIsListEmpty] = useState(false);
  const assignedToMeFeed = useRef();
  const createdByMeFeed = useRef();

  const isCurrentUserAssigneOrCreator = (newFeed) => {
    if (!newFeed._pristineProperties.assignedUserId) {
      return true;
    }
    const loggedUserId = UserService.getLoggedUser().getId();
    return (
      loggedUserId === newFeed._pristineProperties.assignedUserId ||
      loggedUserId === newFeed._pristineProperties.createdUserId
    );
  };

  const onEntityAdded = (newFeed) => {
    setTaskListFeed((taskListFeed) => ({
      ...taskListFeed,
      [newFeed.id]: newFeed,
    }));
  };

  const onEntityRemoved = (oldFeed) => {
    setTaskListFeed((taskListFeed) => ({
      ...taskListFeed,
      [oldFeed.id]: undefined,
    }));
  };

  const loadUsersForCurrentOrganization = async () => {
    const users = await UserService.getUsersForCurrentOrganization();
    setUserOptions(users);
  };

  useEffect(() => {
    loadUsersForCurrentOrganization();
  }, []);

  useEffect(() => {
    const sortOptionFromStorage =
      steveDataStorage("taskList", "sortingOptions", false).get(data.id) ||
      PRIORITY_DESCENDING;
    setSelectedSort(sortOptionFromStorage);
  }, [data.id]);

  useEffect(() => {
    const onFeedPropertiesUpdated = (newFeed) => {
      setTaskListFeed((taskListFeed) => ({
        ...taskListFeed,
        [newFeed.id]: isCurrentUserAssigneOrCreator(newFeed)
          ? newFeed
          : undefined,
      }));
    };

    const addFeedListeners = (feed) => {
      feed.on("EntityPropertiesUpdated", onFeedPropertiesUpdated);
      feed.on("EntityAdded", onEntityAdded);
      feed.on("EntityRemoved", onEntityRemoved);
    };

    const removeFeedListeners = (feed) => {
      if (feed && typeof feed.removeAllListeners === "function") {
        feed.removeAllListeners(
          "EntityPropertiesUpdated",
          onFeedPropertiesUpdated
        );
        feed.removeAllListeners("EntityAdded", onEntityAdded);
        feed.removeAllListeners("EntityRemoved", onEntityRemoved);
      }
    };

    const shutdown = (taskListFeed) => {
      removeFeedListeners();
      if (taskListFeed && typeof taskListFeed.shutdown === "function") {
        taskListFeed.shutdown();
      }
    };

    const loadTaskListFeed = async () => {
      setIsLoadingData(true);
      shutdown(assignedToMeFeed.current);
      shutdown(createdByMeFeed.current);
      assignedToMeFeed.current =
        (await UserService.getAssignedToMeTaskListFeed(formData)) || {};
      createdByMeFeed.current = await UserService.getCreatedByMeTaskList(
        formData || {}
      );
      addFeedListeners(assignedToMeFeed.current);
      addFeedListeners(createdByMeFeed.current);
      setTaskListFeed({
        ...assignedToMeFeed.current._cache,
        ...createdByMeFeed.current._cache,
      });
      setIsLoadingData(false);
    };
    loadTaskListFeed();
    return () => {
      shutdown(assignedToMeFeed.current);
      shutdown(createdByMeFeed.current);
    };
  }, [formData]);

  const toggleSettingsModal = (e) => {
    e.stopPropagation();
    setIsSettingsModalShown(
      (currentIsSettingsModalShown) => !currentIsSettingsModalShown
    );
  };

  const toggleDeleteModal = (e) => {
    e.stopPropagation();
    setIsTaskDetailsModalShown((value) => !value);
    setIsDeleteModalShown((value) => !value);
  };

  const openTaskDetails = (task) => () => {
    setSelectedTask(task);
    setIsTaskDetailsModalShown(true);
  };
  const closeTaskDetails = () => {
    setSelectedTask(null);
    setIsTaskDetailsModalShown(false);
  };

  const toggleTaskConfigModal = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (isEditAllowed) {
      return;
    }
    setIsTaskConfigShown((value) => !value);
  };

  const handleSave = (values) => {
    setFormData(values);
    onUpdate({
      ...data,
      title: values.title,
      config: {
        ...data.config,
        ...values,
      },
    });
    setIsSettingsModalShown(false);
  };

  const updateStatus = async (status) => {
    const task = await UserService.getSelectedTaskEntity(selectedTask);

    if (task._pristineProperties.status === status.value) {
      return;
    }

    task.setStatus(status.value);
    await task.update();

    setSelectedTask(task);
  };

  const createNewTask = async (data) => {
    const createdTaskId = await UserService.createNewTask(data);
    if (createdTaskId) {
      setCreatedTaskId(createdTaskId);
      setIsListEmpty(false);
    }
  };

  const updateTask = async (data) => {
    const task = await UserService.updateTask(data, selectedTask);

    setSelectedTask(task);
  };

  useEffect(() => {
    const checkIfListIsEmpty = (task) => taskListFeed[task] !== undefined;

    if (taskListFeed) {
      setIsListEmpty(!Object.keys(taskListFeed).some(checkIfListIsEmpty));
    }
  }, [taskListFeed]);

  const deleteTask = async () => {
    const success = await UserService.removeTask(selectedTask);
    if (success) {
      setIsTaskDetailsModalShown(false);
    }
    setIsDeleteModalShown(false);
    setSelectedTask(null);
  };

  const changeSorting = (option) => {
    steveDataStorage("taskList", "sortingOptions", false).set(option, data.id);
    setSelectedSort(option);
  };

  return (
    <>
      <div className="glu-dashboard-card glu-content">
        {isLoadingData && <Loader />}
        <div className="glu-content__header">
          {isEditAllowed && (
            <div className="glu-flex-shrink">
              <IconButton
                className="glu-icon__draggable cursor-move"
                icon="drag"
              />
            </div>
          )}
          <TextOverflowTooltip
            title={formData.title}
            height={height}
            width={width}
            renderChildren={(ref) => (
              <div className="glu-flex-grow">
                <div className="glu-page__title glu-widget-title" ref={ref}>
                  {formData.title}
                </div>
              </div>
            )}
          />

          <div className="glu-flex-row">
            {!isEditAllowed && (
              <div className="glu-flex-shrink glu-flex-shrink--large-padding">
                <IconButton
                  icon="dashboard-add"
                  text="New task"
                  onClick={toggleTaskConfigModal}
                />
              </div>
            )}
          </div>
          <div className="glu-flex-row">
            <SortOptions
              selectedSort={selectedSort}
              changeSelectedSort={changeSorting}
            />
          </div>
          <div className="glu-flex-row">
            {isEditAllowed && (
              <WidgetOptions
                showWidgetInfo={showWidgetInfo(data)}
                openConfiguration={toggleSettingsModal}
                onRemove={onRemove(data)}
                onClone={onClone(data)}
              />
            )}
          </div>
        </div>
        <div className="glu-content__body">
          {!isLoadingData &&
          taskListFeed &&
          Object.keys(taskListFeed).length &&
          !isListEmpty ? (
            Object.values(taskListFeed)
              .filter((feed) => feed)
              .sort(getTasksSortingFunction(selectedSort))
              .map((task) => (
                <TaskListItem
                  key={task.getId()}
                  openTaskDetails={openTaskDetails(task)}
                  task={{
                    ...task._pristineProperties,
                    createdOn: task.getCreatedOn(),
                    id: task.getId(),
                  }}
                  createdTaskId={createdTaskId}
                />
              ))
          ) : (
            <WidgetEmpty
              text="There are no tasks in the list"
              isEditAllowed={isEditAllowed}
              showSettingsModal={toggleSettingsModal}
            />
          )}
        </div>
      </div>
      {isSettingsModalShown && (
        <ModalDefault isOpen={isSettingsModalShown}>
          <div className="glu-modal-content">
            <div className="glu-modal__header">
              <div className="glu-modal__title glu-flex-grow">
                {formData.title}
              </div>
              <div className="glu-flex-shrink">
                <button
                  aria-label="close"
                  type="button"
                  className="glu-btn-link g-icon g-icon-close"
                  onClick={toggleSettingsModal}
                />
              </div>
            </div>
            <TaskListConfigForm
              initialValues={formData}
              onCancel={toggleSettingsModal}
              onFormSubmit={handleSave}
              userOptions={userOptions}
            />
          </div>
        </ModalDefault>
      )}
      {isTaskDetailsModalShown && (
        <ModalDefault isOpen={isTaskDetailsModalShown}>
          <TaskListItemDetails
            task={selectedTask.getPristineProperties()}
            createdOn={selectedTask.getCreatedOn()}
            closeDetails={closeTaskDetails}
            onStatusChange={updateStatus}
            onEditClick={toggleTaskConfigModal}
            onDeleteClick={toggleDeleteModal}
            isEditAllowed={isEditAllowed}
          />
        </ModalDefault>
      )}

      {isTaskConfigShown && (
        <ModalDefault isOpen={isTaskConfigShown}>
          <div className="glu-modal-content glu-widget-task-list__modal">
            <div className="glu-modal__header">
              <div className="glu-modal__title glu-flex-grow">
                {selectedTask ? "Edit Task" : "New Task"}
              </div>
              <div className="glu-flex-shrink">
                <button
                  aria-label="close"
                  type="button"
                  className="glu-btn-link g-icon g-icon-close"
                  onClick={toggleTaskConfigModal}
                />
              </div>
            </div>
            <TaskListNewTaskForm
              onSubmit={selectedTask ? updateTask : createNewTask}
              closeForm={toggleTaskConfigModal}
              userOptions={userOptions}
              selectedTask={selectedTask}
            />
          </div>
        </ModalDefault>
      )}
      {isDeleteModalShown && (
        <ModalDelete
          itemLabel={selectedTask._pristineProperties.name}
          isOpen={isDeleteModalShown}
          onDelete={deleteTask}
          onCancel={toggleDeleteModal}
        >
          <div className="glu-h5">
            Are you sure you want to delete task{" "}
            {`"${selectedTask._pristineProperties.name}"`}? <br />
          </div>
        </ModalDelete>
      )}
    </>
  );
};

TaskList.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditAllowed: PropTypes.bool,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  showWidgetInfo: PropTypes.func,
  onClone: PropTypes.func,
};

TaskList.defaultProps = {
  isEditAllowed: false,
  onRemove: undefined,
  onUpdate: undefined,
  showWidgetInfo: undefined,
  onClone: undefined,
};

export default TaskList;
